/**
 * The last line before a white screen.
 *
 * ## Why this exists
 *
 * There was no error boundary anywhere in the app. A render-time throw in any component
 * unmounts the entire React tree — React 18 and later do this deliberately rather than
 * leaving a half-rendered UI — and what the viewer gets is a blank page with no
 * explanation, no way back, and no clue that anything is recoverable. The gap was known:
 * `ProfileSettings.tsx` carries a comment about exactly this, written while working around
 * a crash rather than catching it.
 *
 * That failure is worse here than in most apps. On the television there is no address bar,
 * no reload gesture and no developer console — a white screen means holding the power
 * button, and the app looks broken rather than momentarily wrong.
 *
 * ## What it deliberately does not do
 *
 * It does not report anywhere. This is a private household app with no telemetry and no
 * error service, and adding one would put viewing behaviour on someone else's server —
 * against the whole premise of the project. `console.error` is the record, which is
 * reachable over `sdb dlog` on the panel and in devtools everywhere else.
 *
 * It also does not retry automatically. A component that throws on render usually throws
 * again on the next one, and an automatic retry turns a visible failure into a render loop
 * that burns the panel's CPU silently. The viewer asks, by pressing the button.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /**
   * Resets the boundary when it changes — a route path, typically.
   *
   * Without this, navigating away from a screen that crashed leaves the fallback in place,
   * because the boundary has no reason to believe anything improved. With it, Back works.
   */
  resetKey?: string;
  /** Shown instead of the default panel. The player passes one that keeps its own chrome. */
  fallback?: (retry: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(prev: Props): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The component stack is the useful half and React does not put it on the Error, so
    // both are logged together or the trace points only at the boundary.
    console.error('render failed', error, info.componentStack);
  }

  private readonly retry = (): void => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.retry);

    return (
      <div className="crash" role="alert">
        <strong className="crash__title">Bir şeyler ters gitti</strong>
        <p className="crash__body">
          Bu ekran yüklenemedi. Tekrar denemek sorunu genellikle çözer; çözmezse ana sayfaya
          dönebilirsin.
        </p>
        <div className="crash__actions">
          {/*
            A plain button, not a Link: this renders inside the router, and if the router
            itself is what threw, a Link would throw again on the way out.
          */}
          <button type="button" className="primary" onClick={this.retry}>
            Tekrar dene
          </button>
          <button type="button" onClick={() => (window.location.href = '/')}>
            Ana sayfa
          </button>
        </div>
      </div>
    );
  }
}
